import React, { useMemo } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import {
  MAX_SUGGESTED_PROMPTS,
  PROMPT_MANAGEMENT_CATALOG,
  formatGreetingMessage,
  getMirrorSiteTerminologyDescription,
  suggestedPromptLabels,
  toneDescriptions,
  toneProfiles,
  type GreetingTemplate,
  type SuggestedPromptLabel,
  type ToneProfile,
} from 'skipify-types';

import { useMerchant } from '../../../../contexts/MerchantContext';
import {
  useGetAgenticVoiceProfilesQuery,
  useUploadAgenticAvatarMutation,
} from '../../../../store/modules/merchant/api';
import { toast } from '../../../../util/toast';
import { AgentVoiceSelector } from '../components/AgentVoiceSelector';
import LogoInput from '../../../Branding/LogoInput';
import {
  AGENT_AVATARS,
  CUSTOM_AVATAR_OPTION_ID,
  DEFAULT_AGENT_VOICE_PROFILE_ID,
  isCatalogAvatarSrc,
  isCustomAvatarSrc,
  uploadedAvatarSrcFromResponse,
  voiceLabelForProfileId,
  voicesFromProfileIds,
} from '../../agentCatalog';
import { exampleBoxSx, radioSx, selectionBoxSx } from '../../ellaFormStyles';
import {
  ELLA_SDK_DEFAULT_BRANDING,
  EllaOnboardingPreview,
} from '../components/EllaOnboardingPreview';
import { OnboardingColorField } from '../components/OnboardingColorField';
import { type AgentSetupConfig, useOnboardingDraft } from '../OnboardingDraftContext';

const EXAMPLE_BOX_SX = exampleBoxSx('#F5F5F5');

const avatarCropSx = {
  objectFit: 'cover' as const,
  objectPosition: 'center top',
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const formatToneLabel = (tone: ToneProfile) => capitalize(tone);

const getSaveErrorMessage = (err: unknown, fallback: string): string => {
  const errors = (err as { data?: { errors?: string[] } })?.data?.errors;
  return Array.isArray(errors) && errors.length > 0 ? errors.join(' ') : fallback;
};

const AvatarCircle = ({
  src,
  size,
  alt = '',
  placeholder = false,
}: {
  src?: string;
  size: number;
  alt?: string;
  placeholder?: boolean;
}): JSX.Element => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      overflow: 'hidden',
      flexShrink: 0,
      bgcolor: 'grey.200',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {src ? (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{ width: '100%', height: '100%', ...avatarCropSx }}
      />
    ) : placeholder ? (
      <AutoAwesomeIcon sx={{ fontSize: size * 0.34, color: 'brand.main' }} aria-hidden />
    ) : null}
  </Box>
);

export const AgentSetupStep = (): JSX.Element => {
  const { merchantId } = useMerchant();
  const { draft, updateDraft } = useOnboardingDraft();
  const config = draft.agentSetup;
  const [customAvatarSelected, setCustomAvatarSelected] = React.useState(false);
  const [replaceAvatarOpen, setReplaceAvatarOpen] = React.useState(false);
  const [uploadAgenticAvatar, { isLoading: isUploadingAvatar }] = useUploadAgenticAvatarMutation();
  const { data: voiceProfiles, isLoading: isLoadingVoiceProfiles } =
    useGetAgenticVoiceProfilesQuery(merchantId, {
      skip: !merchantId,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    });

  const catalogVoices = useMemo(
    () => voicesFromProfileIds(voiceProfiles?.voiceProfileIds),
    [voiceProfiles?.voiceProfileIds],
  );
  const defaultVoiceProfileId = catalogVoices[0]?.profileId ?? DEFAULT_AGENT_VOICE_PROFILE_ID;

  React.useEffect(() => {
    if (!defaultVoiceProfileId || config.voiceProfileId.trim()) {
      return;
    }

    updateDraft((prev) => ({
      ...prev,
      agentSetup: { ...prev.agentSetup, voiceProfileId: defaultVoiceProfileId },
    }));
  }, [config.voiceProfileId, defaultVoiceProfileId, updateDraft]);

  const voiceOptions = useMemo(() => {
    if (catalogVoices.some((voice) => voice.profileId === config.voiceProfileId)) {
      return catalogVoices;
    }
    return [
      ...catalogVoices,
      {
        profileId: config.voiceProfileId,
        label: voiceLabelForProfileId(config.voiceProfileId, catalogVoices),
      },
    ];
  }, [catalogVoices, config.voiceProfileId]);

  const selectedIsCustomAvatar = customAvatarSelected || isCustomAvatarSrc(config.avatarSrc);
  const avatarSelectValue =
    AGENT_AVATARS.find((avatar) => avatar.src === config.avatarSrc)?.src ??
    (selectedIsCustomAvatar ? CUSTOM_AVATAR_OPTION_ID : '');
  const showCustomAvatarUploader =
    selectedIsCustomAvatar && (replaceAvatarOpen || !config.avatarSrc);

  const greetingOptions = useMemo(
    () =>
      PROMPT_MANAGEMENT_CATALOG.greetingTemplates.values.map((templateKey) => ({
        key: templateKey,
        label: formatGreetingMessage(templateKey, config.agentName.trim() || 'Ella'),
      })),
    [config.agentName],
  );

  const promptSlots = useMemo<(SuggestedPromptLabel | '')[]>(() => {
    return Array.from(
      { length: MAX_SUGGESTED_PROMPTS },
      (_, index) => config.suggestedPrompts[index] ?? '',
    );
  }, [config.suggestedPrompts]);

  const toneDescription = toneDescriptions[config.tone] ?? '';

  const updateConfig = (patch: Partial<AgentSetupConfig>) => {
    updateDraft((prev) => ({
      ...prev,
      agentSetup: { ...prev.agentSetup, ...patch },
    }));
  };

  const onAvatarOptionChange = (value: string) => {
    if (value === CUSTOM_AVATAR_OPTION_ID) {
      setCustomAvatarSelected(true);
      if (isCatalogAvatarSrc(config.avatarSrc)) {
        updateConfig({ avatarSrc: '' });
        setReplaceAvatarOpen(true);
        return;
      }
      setReplaceAvatarOpen(!isCustomAvatarSrc(config.avatarSrc));
      return;
    }
    setCustomAvatarSelected(false);
    setReplaceAvatarOpen(false);
    updateConfig({ avatarSrc: value });
  };

  const handleCustomAvatarFile = async (file: File) => {
    if (!merchantId) {
      return;
    }
    try {
      const result = await uploadAgenticAvatar({ merchantId, file }).unwrap();
      const uploadedSrc = uploadedAvatarSrcFromResponse(result);
      if (!uploadedSrc) {
        toast.error('Failed to upload avatar');
        return;
      }
      updateConfig({ avatarSrc: uploadedSrc });
      setCustomAvatarSelected(true);
      setReplaceAvatarOpen(false);
    } catch (err) {
      toast.error(getSaveErrorMessage(err, 'Failed to upload avatar'));
    }
  };

  const onPromptChange = (index: number, value: SuggestedPromptLabel | '') => {
    const next = [...promptSlots];
    next[index] = value;
    updateConfig({
      suggestedPrompts: next.filter((prompt): prompt is SuggestedPromptLabel => prompt !== ''),
    });
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
          gap: { xs: 4, lg: 5 },
          alignItems: 'start',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box component="section" data-testid="ella-onboarding-agent-details-section">
            <Typography component="h3" variant="small" fontWeight="bold" sx={{ mb: 2 }}>
              Agent details
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Agent name"
                value={config.agentName}
                onChange={(event) => updateConfig({ agentName: event.target.value })}
                fullWidth
                inputProps={{ 'data-testid': 'ella-onboarding-agent-name' }}
              />

              <Box>
                <Typography variant="small" fontWeight="bold" sx={{ mb: 1.5 }}>
                  Agent avatar
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}
                >
                  <FormControl fullWidth sx={{ flex: 1, minWidth: 200 }}>
                    <InputLabel id="ella-onboarding-agent-avatar-label" shrink>
                      Select an avatar option
                    </InputLabel>
                    <Select
                      labelId="ella-onboarding-agent-avatar-label"
                      label="Select an avatar option"
                      notched
                      displayEmpty
                      value={avatarSelectValue}
                      onChange={(event: SelectChangeEvent) =>
                        onAvatarOptionChange(event.target.value)
                      }
                      inputProps={{ 'aria-label': 'Agent avatar' }}
                      data-testid="ella-onboarding-agent-avatar"
                    >
                      {AGENT_AVATARS.map((option) => (
                        <MenuItem key={option.src} value={option.src}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AvatarCircle src={option.src} size={24} />
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                      <MenuItem value={CUSTOM_AVATAR_OPTION_ID}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AvatarCircle
                            src={isCustomAvatarSrc(config.avatarSrc) ? config.avatarSrc : ''}
                            size={24}
                            placeholder
                          />
                          Custom avatar
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Box
                    sx={{ mx: 'auto', width: 70, position: 'relative', flexShrink: 0 }}
                    data-testid="ella-onboarding-agent-avatar-preview"
                  >
                    <AvatarCircle
                      src={config.avatarSrc}
                      size={70}
                      alt="Agent avatar preview"
                      placeholder={selectedIsCustomAvatar}
                    />
                    {selectedIsCustomAvatar && config.avatarSrc && (
                      <IconButton
                        aria-label="Edit custom avatar"
                        data-testid="ella-onboarding-edit-custom-avatar"
                        onClick={() => {
                          setCustomAvatarSelected(true);
                          setReplaceAvatarOpen(true);
                        }}
                        sx={{
                          position: 'absolute',
                          right: -6,
                          bottom: -6,
                          width: 28,
                          height: 28,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'grey.400',
                          boxShadow: 1,
                          '&:hover': { bgcolor: 'grey.100' },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {showCustomAvatarUploader && (
                  <Box sx={{ mt: 2 }} data-testid="ella-onboarding-custom-avatar-uploader">
                    <Typography
                      variant="small"
                      color="text.secondary"
                      sx={{ mb: 1.5, display: 'block' }}
                    >
                      Upload a custom avatar. 70x70px max, PNG or JPEG.
                    </Typography>
                    <LogoInput
                      img={config.avatarSrc || null}
                      disabled={isUploadingAvatar}
                      ariaLabel="Custom avatar"
                      imageAlt="Custom avatar"
                      uploadText="Upload an avatar"
                      replaceText="Replace avatar"
                      removeText="Remove avatar"
                      onChange={(value) => {
                        if (value instanceof File) {
                          void handleCustomAvatarFile(value);
                        }
                      }}
                      onDelete={() => {
                        updateConfig({ avatarSrc: '' });
                        setCustomAvatarSelected(true);
                        setReplaceAvatarOpen(true);
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Box>
                <Typography variant="small" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Tone
                </Typography>
                <Typography variant="small" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Select how your agent should sound.
                </Typography>
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mb: 2 }}
                  role="radiogroup"
                  aria-label="Tone"
                >
                  {toneProfiles.map((profile) => {
                    const isSelectedTone = config.tone === profile;
                    return (
                      <Box
                        key={profile}
                        component="button"
                        type="button"
                        role="radio"
                        aria-checked={isSelectedTone}
                        aria-label={formatToneLabel(profile)}
                        onClick={() => updateConfig({ tone: profile })}
                        data-testid={`ella-onboarding-tone-${profile}`}
                        sx={{
                          ...selectionBoxSx(isSelectedTone, false),
                          minWidth: 140,
                          flex: 1,
                          height: 52,
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: '27px',
                        }}
                      >
                        {formatToneLabel(profile)}
                      </Box>
                    );
                  })}
                </Box>
                <Typography variant="small" fontWeight="bold" sx={{ mb: 1 }}>
                  Example tone: {formatToneLabel(config.tone)}
                </Typography>
                <Box sx={EXAMPLE_BOX_SX}>
                  <Typography variant="small" color="text.secondary">
                    {capitalize(toneDescription)}
                  </Typography>
                </Box>
              </Box>

              <AgentVoiceSelector
                merchantId={merchantId}
                agentDisplayName={config.agentName}
                voiceProfileId={config.voiceProfileId}
                defaultVoiceProfileId={defaultVoiceProfileId}
                voiceOptions={voiceOptions}
                loadingVoices={isLoadingVoiceProfiles}
                onVoiceProfileIdChange={(profileId) => updateConfig({ voiceProfileId: profileId })}
                voiceSelectTestId="ella-onboarding-agent-voice"
                playVoiceTestId="ella-onboarding-play-voice"
              />
            </Box>
          </Box>

          <Box component="section" data-testid="ella-onboarding-greeting-section">
            <Typography component="h3" variant="small" fontWeight="bold" sx={{ mb: 2 }}>
              Greeting & prompts
            </Typography>
            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <InputLabel id="ella-onboarding-greeting-label" shrink>
                Select a greeting
              </InputLabel>
              <Select
                labelId="ella-onboarding-greeting-label"
                label="Select a greeting"
                notched
                value={config.customGreeting}
                onChange={(event: SelectChangeEvent) =>
                  updateConfig({ customGreeting: event.target.value as GreetingTemplate })
                }
                data-testid="ella-onboarding-opening-message"
              >
                {greetingOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="small" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Prompts
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {promptSlots.map((prompt, index) => (
                <FormControl key={`prompt-${index}`} fullWidth>
                  <InputLabel id={`ella-onboarding-prompt-${index}-label`} shrink>
                    {`Prompt ${index + 1}`}
                  </InputLabel>
                  <Select
                    labelId={`ella-onboarding-prompt-${index}-label`}
                    label={`Prompt ${index + 1}`}
                    notched
                    value={prompt}
                    displayEmpty
                    onChange={(event: SelectChangeEvent) =>
                      onPromptChange(index, event.target.value as SuggestedPromptLabel | '')
                    }
                    inputProps={{ 'data-testid': `ella-onboarding-prompt-input-${index}` }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {suggestedPromptLabels.map((label) => (
                      <MenuItem
                        key={label}
                        value={label}
                        disabled={promptSlots.includes(label) && promptSlots[index] !== label}
                      >
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </Box>
          </Box>

          <Box component="section" data-testid="ella-onboarding-conversation-rules-section">
            <Typography component="h3" variant="small" fontWeight="bold" sx={{ mb: 2 }}>
              Conversation rules
            </Typography>

            <Typography variant="small" fontWeight="bold" sx={{ mb: 0.5 }}>
              Brand vocabulary
            </Typography>
            <Typography variant="small" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Choose whether the agent mirrors storefront terminology or uses universal language.
            </Typography>
            <RadioGroup
              value={config.mirrorSiteTerminology ? 'true' : 'false'}
              onChange={(_event, value) =>
                updateConfig({ mirrorSiteTerminology: value === 'true' })
              }
              sx={{ mb: 3, gap: 2 }}
            >
              {(
                [
                  {
                    value: 'true',
                    title: "Use storefront's terminology",
                    description: getMirrorSiteTerminologyDescription(true),
                  },
                  {
                    value: 'false',
                    title: 'Use universal language',
                    description: getMirrorSiteTerminologyDescription(false),
                  },
                ] as const
              ).map((option) => (
                <Box
                  key={option.value}
                  component="label"
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, cursor: 'pointer' }}
                >
                  <Radio
                    size="small"
                    value={option.value}
                    checked={config.mirrorSiteTerminology === (option.value === 'true')}
                    sx={{ ...radioSx, mt: '2px' }}
                  />
                  <Box>
                    <Typography variant="medium" fontWeight="semibold">
                      {option.title}
                    </Typography>
                    <Typography variant="small" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </RadioGroup>
          </Box>

          <Box component="section" data-testid="ella-onboarding-branding-section">
            <Typography component="h3" variant="small" fontWeight="bold" sx={{ mb: 0.5 }}>
              Branding
            </Typography>
            <Typography variant="small" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Widget colors are not configurable during onboarding yet.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 2,
                opacity: 0.6,
              }}
            >
              <OnboardingColorField
                label="Thread background"
                value={ELLA_SDK_DEFAULT_BRANDING.threadBackground}
                onChange={() => undefined}
                disabled
                dataTestId="ella-onboarding-color-thread-background"
              />
              <OnboardingColorField
                label="Brand accent"
                value={ELLA_SDK_DEFAULT_BRANDING.brandAccent}
                onChange={() => undefined}
                disabled
                dataTestId="ella-onboarding-color-brand-accent"
              />
              <OnboardingColorField
                label="Text"
                value={ELLA_SDK_DEFAULT_BRANDING.textColor}
                onChange={() => undefined}
                disabled
                dataTestId="ella-onboarding-color-text"
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 24 } }}>
          <Typography variant="small" fontWeight="bold" sx={{ mb: 2 }}>
            Preview
          </Typography>
          <EllaOnboardingPreview
            agentName={config.agentName}
            avatarSrc={config.avatarSrc}
            customGreeting={config.customGreeting}
            suggestedPrompts={config.suggestedPrompts}
          />
        </Box>
      </Box>
    </>
  );
};
